import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RiskBadge } from "@/components/RiskBadge";
import { statusColor } from "@/lib/constants";
import {
  Loader2, Mic, ShieldCheck, TrendingUp, Dna, ArrowUpRight, Activity, ChevronRight, ShieldAlert,
  FlaskConical, MapPin, CreditCard, X,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const RISK_NUM = { Mild: 1, Moderate: 2, High: 3 };

export default function PatientDashboard() {
  const { user } = useAuth();
  const [triage, setTriage] = useState(null);
  const [tests, setTests] = useState([]);
  const [orderTest, setOrderTest] = useState(null);

  const load = async () => {
    const [t, g] = await Promise.all([api.get("/triage/me"), api.get("/genetic/me")]);
    setTriage(t.data.triage);
    setTests(g.data.tests);
  };
  useEffect(() => { load(); }, []);

  const testByCond = useMemo(() => Object.fromEntries(tests.map((x) => [x.condition_id, x])), [tests]);

  if (!triage) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const consentOk = user.consent_status === "accepted";
  const screened = triage.some((t) => t.latest);
  const escalations = triage.filter((t) => t.escalation);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">MANOGENE ID · <span className="mono text-brand">{user.manogene_id}</span></div>
          <h1 className="font-head text-3xl font-bold text-ink mt-1">Hello, {user.name.split(" ")[0]}</h1>
          <p className="text-ink-soft mt-1">Your voice triage and genetic pathway across 8 conditions.</p>
        </div>
        <Link to="/patient/record" data-testid="dash-record-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-brand text-white px-5 py-3 font-semibold hover:bg-brand-hover transition-colors">
          <Mic className="h-4 w-4" /> New voice screening
        </Link>
      </div>

      {!consentOk && (
        <Link to="/patient/consent" className="mt-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-amber-800 card-lift" data-testid="consent-warning">
          <ShieldAlert className="h-5 w-5" />
          <span className="text-sm font-medium flex-1">Consent required before screening. Tap to review and accept.</span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      )}

      {escalations.length > 0 && (
        <div className="mt-6 rounded-xl border border-red-200 bg-[#FEF2F2] px-4 py-3.5" data-testid="escalation-banner">
          <div className="flex items-center gap-2 text-[#B91C1C] font-semibold text-sm">
            <TrendingUp className="h-4.5 w-4.5 h-[18px] w-[18px]" /> {escalations.length} escalation{escalations.length > 1 ? "s" : ""} detected
          </div>
          <div className="mt-1 text-sm text-red-700">
            {escalations.map((e) => `${e.condition.name} (${e.escalation.from}→${e.escalation.to})`).join(", ")}
          </div>
        </div>
      )}

      {!screened && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <Activity className="h-10 w-10 text-brand mx-auto mb-3" />
          <h3 className="font-head text-xl font-semibold text-ink">No screening yet</h3>
          <p className="text-ink-soft mt-1 max-w-md mx-auto">Record a short voice sample to generate your first triage across all conditions.</p>
          <Link to="/patient/record" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand text-white px-5 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
            <Mic className="h-4 w-4" /> Record now
          </Link>
        </div>
      )}

      {screened && (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {triage.map(({ condition, latest, history, escalation }) => {
            const test = testByCond[condition.id];
            const sc = test ? statusColor(test.status) : null;
            const chartData = history.map((h, i) => ({ i: i + 1, v: RISK_NUM[h.risk], risk: h.risk }));
            return (
              <div key={condition.id} data-testid={`condition-card-${condition.id}`}
                className="rounded-xl border border-slate-200 bg-white p-5 card-lift flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="eyebrow">{condition.category}</div>
                    <h3 className="font-head font-semibold text-ink text-lg leading-tight mt-0.5">{condition.name}</h3>
                  </div>
                  {escalation && <TrendingUp className="h-4 w-4 text-red-500" title="Escalation" />}
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <span className="eyebrow">Voice triage</span>
                  <RiskBadge risk={latest?.risk} size="sm" testId={`triage-${condition.id}`} />
                </div>

                {chartData.length > 1 && (
                  <div className="h-12 mt-2">
                    <ResponsiveContainer width="100%" height={44} minWidth={0}>
                      <LineChart data={chartData} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
                        <Line type="monotone" dataKey="v" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 2 }} />
                        <YAxis hide domain={[0.5, 3.5]} />
                        <XAxis dataKey="i" hide />
                        <Tooltip formatter={(v, n, p) => p.payload.risk} labelFormatter={() => ""} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="eyebrow flex items-center gap-1"><Dna className="h-3 w-3" /> Genetic</span>
                  {test && (
                    <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold mono"
                      style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}
                      data-testid={`gstatus-${condition.id}`}>
                      {test.status}
                    </span>
                  )}
                </div>

                {test && test.status === "Recommended" && (
                  <button onClick={() => setOrderTest(test)}
                    data-testid={`order-genetic-${condition.id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand text-white text-sm font-semibold py-2 hover:bg-brand-hover transition-colors">
                    <ArrowUpRight className="h-3.5 w-3.5" /> Order genetic test
                  </button>
                )}
                {test && test.status === "In Lab" && (
                  <div className="mt-3 rounded-lg bg-[#F5F3FF] border border-[#DDD6FE] px-3 py-2 text-xs font-medium text-[#5B21B6] flex items-center gap-1.5">
                    <FlaskConical className="h-3.5 w-3.5" /> Sample with lab — report pending
                  </div>
                )}
                {test && test.clinician_review && (
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs text-ink-soft">
                    <span className="font-semibold text-ink">Clinician:</span> {test.clinician_review.decision}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {orderTest && <GeneticOrderModal test={orderTest} onClose={() => setOrderTest(null)} />}
    </div>
  );
}

function GeneticOrderModal({ test, onClose }) {
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const pay = async () => {
    if (!addr.line1 || !addr.city || !addr.postal_code) return toast.error("Please fill address line 1, city and postal code");
    if (!date) return toast.error("Please choose a preferred sample-collection date");
    setBusy(true);
    try {
      const { data } = await api.post(`/genetic/${test.id}/checkout`, { address: addr, collection_date: date, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="order-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head text-xl font-semibold text-ink">Order genetic test</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-ink-soft mb-4 flex items-center gap-1.5">
          <MapPin className="h-4 w-4 text-brand" /> A kit is sent to your home for sample collection.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="text-sm font-medium text-ink-soft">Address line 1</span>
            <input value={addr.line1} onChange={set("line1")} data-testid="addr-line1"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
          </label>
          <label className="col-span-2 block">
            <span className="text-sm font-medium text-ink-soft">Address line 2 (optional)</span>
            <input value={addr.line2} onChange={set("line2")}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink-soft">City</span>
            <input value={addr.city} onChange={set("city")} data-testid="addr-city"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink-soft">State</span>
            <input value={addr.state} onChange={set("state")}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink-soft">Postal code</span>
            <input value={addr.postal_code} onChange={set("postal_code")} data-testid="addr-postal"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink-soft">Country</span>
            <input value={addr.country} onChange={set("country")}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
          </label>
        </div>

        <label className="block mt-3">
          <span className="text-sm font-medium text-ink-soft">Preferred sample-collection date</span>
          <input type="date" value={date} min={new Date().toISOString().split("T")[0]}
            onChange={(e) => setDate(e.target.value)} data-testid="collection-date"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand" />
        </label>

        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
          <span className="text-sm text-ink-soft">Genetic test ({(test.recommended_tests || []).map((x) => x.gene).join(", ") || "panel"})</span>
          <span className="font-head font-bold text-ink">$149.00</span>
        </div>

        <button onClick={pay} disabled={busy} data-testid="order-pay-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand text-white py-2.5 font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          Pay & confirm order
        </button>
        <p className="mt-2 text-center text-xs text-ink-muted">Secure checkout via Stripe · test card 4242 4242 4242 4242</p>
      </div>
    </div>
  );
}
